import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, ArrowRight, Home, Search, MessageCircle, BookOpen, Phone } from 'lucide-react';

export default function GraciasPage() {
  const [searchParams] = useSearchParams();
  const source = searchParams.get('from') || 'contacto';
  
  // Google Tag Manager - Push conversion event to dataLayer
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Push event to GTM dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'form_submission',
        'form_type': source,
        'page_path': '/gracias'
      });
      
      // Also fire gtag event if available (for direct GA4/Ads tracking)
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'event_category': 'form_submission',
          'event_label': source
        });
      }
    }
  }, [source]);

  const getContent = () => {
    switch(source) {
      case 'centro':
        return {
          title: '¡Solicitud de Centro Recibida!',
          subtitle: 'Tu centro está en proceso de revisión',
          message: 'Nuestro equipo revisará la información de tu centro y te contactaremos dentro de 24-48 horas hábiles para activar tu perfil.',
          icon: <CheckCircle className="w-20 h-20 text-emerald-400" />,
          cta: { text: 'Ver Directorio de Centros', link: '/centros-de-rehabilitacion-drogas-chile' }
        };
      case 'profesional':
        return {
          title: '¡Solicitud de Profesional Recibida!',
          subtitle: 'Tu perfil está en proceso de revisión',
          message: 'Revisaremos tu información y credenciales. Te contactaremos dentro de 24-48 horas hábiles para activar tu perfil profesional.',
          icon: <CheckCircle className="w-20 h-20 text-emerald-400" />,
          cta: { text: 'Ver Directorio de Profesionales', link: '/profesionales' }
        };
      case 'plan-referidos':
        return {
          title: '¡Solicitud de Plan Recibida!',
          subtitle: 'Pronto te contactaremos',
          message: 'Hemos recibido tu interés en el Plan por Referidos. Un ejecutivo se comunicará contigo dentro de las próximas 24 horas para explicarte todos los beneficios.',
          icon: <CheckCircle className="w-20 h-20 text-amber-400" />,
          cta: { text: 'Ver Nuestros Planes', link: '/planes' }
        };
      default:
        return {
          title: '¡Mensaje Enviado!',
          subtitle: 'Gracias por contactarnos',
          message: 'Hemos recibido tu mensaje y te responderemos a la brevedad posible. Normalmente respondemos dentro de 24 horas hábiles.',
          icon: <CheckCircle className="w-20 h-20 text-emerald-400" />,
          cta: { text: 'Volver al Inicio', link: '/' }
        };
    }
  };

  const content = getContent();

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Gracias | SinAdicciones.org</title>
        <meta name="robots" content="noindex, nofollow" />
      
      <Header />
      
      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            {/* Success Animation */}
            <div className="mb-8 animate-bounce-slow">
              {content.icon}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {content.title}
            </h1>
            <p className="text-xl text-emerald-400 mb-6">
              {content.subtitle}
            </p>
            <p className="text-lg text-slate-400 mb-8 max-w-lg mx-auto">
              {content.message}
            </p>
            
            {/* Primary CTA */}
            <Link to={content.cta.link}>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 text-lg">
                {content.cta.text}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* What's Next Section */}
        <section className="py-12 px-4 bg-slate-900/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-8">
              ¿Qué puedes hacer mientras tanto?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/">
                <Card className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all h-full group">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-500/20 transition-colors">
                      <Home className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-2">Explorar Inicio</h3>
                    <p className="text-sm text-slate-400">Descubre todas nuestras herramientas</p>
                  </CardContent>
                </Card>
              </Link>
              
              <Link to="/centros-de-rehabilitacion-drogas-chile">
                <Card className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all h-full group">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-sky-500/20 transition-colors">
                      <Search className="w-6 h-6 text-sky-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-2">Buscar Centros</h3>
                    <p className="text-sm text-slate-400">Encuentra centros de rehabilitación</p>
                  </CardContent>
                </Card>
              </Link>
              
              <Link to="/blog">
                <Card className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all h-full group">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500/20 transition-colors">
                      <BookOpen className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-2">Leer Blog</h3>
                    <p className="text-sm text-slate-400">Artículos sobre adicciones</p>
                  </CardContent>
                </Card>
              </Link>
              
              <a href="https://wa.me/56928341147" target="_blank" rel="noopener noreferrer">
                <Card className="bg-slate-800/50 border-slate-700 hover:border-green-500/50 transition-all h-full group">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-green-500/20 transition-colors">
                      <Phone className="w-6 h-6 text-green-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-2">WhatsApp</h3>
                    <p className="text-sm text-slate-400">Habla con nosotros ahora</p>
                  </CardContent>
                </Card>
              </a>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-12 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
              <CardContent className="p-8">
                <MessageCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  ¿Necesitas ayuda urgente?
                </h3>
                <p className="text-slate-400 mb-6">
                  Si tienes una emergencia o necesitas hablar con alguien ahora mismo, 
                  no dudes en contactarnos directamente por WhatsApp.
                </p>
                <a 
                  href="https://wa.me/56928341147?text=Hola,%20necesito%20ayuda%20urgente"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-green-500 hover:bg-green-600 text-white">
                    <Phone className="w-4 h-4 mr-2" />
                    Contactar por WhatsApp
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      
      <Footer />
      
      {/* Custom CSS for animation */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
